/**
 * Rollen-Typ — plattformfreier Kern (0.12).
 *
 * Vorher definiert in AuthProvider.tsx (Web-Context). adminNav braucht
 * den Typ in core; AuthProvider importiert und re-exportiert ihn, alle
 * bestehenden Importer (@/features/auth/useAuth) bleiben gültig.
 *
 * 'chatbot' steht bewusst außerhalb der Rang-Hierarchie
 * (staff < manager < admin) — siehe Migrationen 0067/0068.
 * 'ape_mitarbeiter' ebenso (Runde 5, E-47, Migration 0112): die Aushilfe bei
 * Veranstaltungen sieht NUR ihren zugewiesenen Ape-Verkauf.
 *
 * Wer eine Rolle ergänzt: B-Z11 in tests/unit/apeMitarbeiterRolle.test.ts
 * findet jede Rollenliste im Repo, die sie vergisst.
 */
export type StaffRole =
  | 'admin'
  | 'manager'
  | 'staff'
  | 'chatbot'
  | 'ape_mitarbeiter';

/**
 * Darf dieses Konto Schichtleitungs-Dinge tun (Tag freischalten, Kontor
 * öffnen, absagen)? FAIL-CLOSED: alles außer admin/manager ist ein Nein,
 * ausdrücklich auch `null` (Rolle noch nicht geladen oder gar keine).
 *
 * Warum als Funktion in core und nicht als Ausdruck in der Fläche: der
 * Vergleich stand in apps/pass an mehreren Stellen wörtlich verdoppelt,
 * und in apps/pass ist nichts davon testbar (vitest lädt nur
 * tests/unit/**, kein RN-Preset). Hier ist er einmal formuliert und
 * geprüft.
 *
 * ⚠️ Das ist eine SICHTBARKEITS-Regel für die Oberfläche, kein
 * Sicherheitstor. Das echte Tor ist der Server:
 * `requireStaffRole(['admin','manager'])` gegen den Bearer-Token, und
 * RLS dahinter. Ein Client-Check kann immer umgangen werden.
 */
export function isSchichtleitung(role: StaffRole | null | undefined): boolean {
  return role === 'admin' || role === 'manager';
}

/**
 * Die Rangordnung: staff < manager < admin. `chatbot` steht bewusst
 * AUSSERHALB (Rang 0) — der Bot-Betreiber (0067/0068) ist kein
 * Vorgesetzter von niemandem, er sieht nur seine eigenen Flächen.
 *
 * ⚠️ Diese Tabelle stand bis 05.08.2026 an ZWEI Orten: hier (früher in
 * `navigation/adminNav.ts`) und noch einmal wörtlich in
 * `src/features/auth/RequireAuth.tsx` als lokale `hierarchy`. Zwei
 * Kopien einer Rangordnung sind eine Zeitbombe: wer eine Rolle
 * hinzufügt, ändert die eine und übersieht die andere — und dann
 * entscheidet die Navigation anders als der Routen-Wächter. Sie liegt
 * jetzt hier, weil Ränge fundamentaler sind als Navigation, und
 * `adminNav.ts` reicht sie nur weiter. Festgenagelt in
 * `tests/unit/roleRank.test.ts`.
 */
export const STAFF_ROLE_RANK: Record<StaffRole, number> = {
  /** Bot-Betreiber (0067): kein Rang — sieht nur allowChatbot-Items. */
  chatbot: 0,
  /**
   * Ape-Aushilfe (Runde 5, E-47): ebenfalls kein Rang — sieht nur
   * allowApeMitarbeiter-Items. „Sieht weniger als ein Kellner" ist eine andere
   * Menge, keine niedrigere Stufe; Muster und Begründung wie bei `chatbot`.
   */
  ape_mitarbeiter: 0,
  staff: 1,
  manager: 2,
  admin: 3,
};

/**
 * Reicht der Rang dieses Kontos für `min`?
 *
 * FAIL-CLOSED an drei Stellen:
 *   - `null`/`undefined` (Rolle noch nicht geladen) → nein. Ein „noch
 *     unbekannt" ist kein „darf".
 *   - unbekannte Rolle → nein.
 *   - `chatbot` → Rang 0, also nie ausreichend.
 *
 * ⚠️ Die Richtung des Vergleichs ist hier die eigentliche Sicherung, und
 * das ist unintuitiv. Geprüft wird „Rang REICHT" (`>=`), nicht „Rang
 * reicht NICHT" (`<`). Der Unterschied entscheidet über fail-open vs.
 * fail-closed, sobald ein Rang `undefined` ist:
 *
 *     undefined >= 2   → false   ⇒ abgelehnt   (richtig)
 *     !(undefined < 2) → true    ⇒ durchgelassen (der Fehler, der bis
 *                                  zum Fix in RequireAuth stand)
 *
 * Beide Zeilen lesen sich wie dasselbe. Sie sind es nicht — jeder
 * Vergleich mit `undefined` ergibt `false`, und die Verneinung dreht das
 * um. Der `?? 0`-Fallback macht die Absicht zusätzlich sichtbar; die
 * Sicherung ist aber die Vergleichsrichtung, nicht er.
 *
 * ⚠️ Sichtbarkeits-Regel für die Oberfläche, KEIN Sicherheitstor. Das
 * echte Tor ist `requireStaffRole()` auf dem Server plus RLS.
 */
export function hatMindestensRang(
  role: StaffRole | null | undefined,
  min: StaffRole,
): boolean {
  if (!role) return false;
  return (STAFF_ROLE_RANK[role] ?? 0) >= STAFF_ROLE_RANK[min];
}

/**
 * Ist dieser Text eine bekannte Rolle? — ohne Cast.
 *
 * `staff_profiles.role` ist in der Datenbank `text`. Wer über alle Rollen
 * laufen will, nimmt `Object.keys(STAFF_ROLE_RANK).filter(istStaffRolle)`: So
 * wächst die Liste mit jeder neuen Rolle von selbst mit, statt als
 * abgeschriebenes Feld eine zu vergessen (Runde 5).
 */
export function istStaffRolle(wert: string | null | undefined): wert is StaffRole {
  return typeof wert === 'string' && Object.prototype.hasOwnProperty.call(STAFF_ROLE_RANK, wert);
}

/**
 * Die Rollen, die einen Schichtplan bekommen — im Versand und im
 * Einladungsformular (Kontaktfelder für den Versand).
 *
 * ⚠️ POSITIVLISTE (Runde 5, B-3). Bis dahin stand an diesen Stellen „alle
 * außer admin" bzw. „alle außer admin und chatbot". Jede neue Rolle — zuerst
 * die Ape-Aushilfe — wäre still in den Schichtplan-Versand gerutscht, und die
 * Einwilligung dafür steht per Vorgabe auf `true`.
 */
export const SCHICHTPLAN_ROLLEN = ['staff', 'manager'] as const;

export function bekommtSchichtplan(role: StaffRole | null | undefined): boolean {
  return SCHICHTPLAN_ROLLEN.some((r) => r === role);
}

/**
 * Darf dieses Konto Wartelisten-Einträge LÖSCHEN? (Betriebslücken-Plan,
 * Teil B.) Löschen ist irreversibel und betrifft fremde Gästedaten —
 * „Abgesagt" (reversibel) darf dagegen jede Rolle.
 *
 * ⚠️ F-07: Der Kundenentscheid ist OFFEN — bis zur Antwort gilt Tils
 * Übergangsregel vom 17.08.: ab Manager (vorsichtige Auslegung; das Web
 * war vorher offener als die App). Die Kundenantwort kostet GENAU EINE
 * Zeile, nämlich die return-Zeile hier:
 *   „alle Rollen"  → return hatMindestensRang(role, 'staff');
 *   „ab Manager"   → bleibt wie es ist
 *   „nur Inhaber"  → return hatMindestensRang(role, 'admin');
 * NUR diese Zeile ändern — sie speist App-Knopf (warteliste.tsx),
 * Web-Knopf (WaitlistSection.tsx) UND Server (api/admin-waitlist-delete).
 * Die Wächter in tests/unit/waitlistAdmin.test.ts prüfen die BINDUNG an
 * diese Funktion, nicht ihren Wert — sie bleiben bei jeder Antwort grün.
 */
export function darfWartelisteLoeschen(role: StaffRole | null | undefined): boolean {
  return isSchichtleitung(role);
}

/** Die Arbeitsumgebungen der App. Reihenfolge = aufsteigender Rang. */
export type Cockpit = 'service' | 'manager' | 'inhaber' | 'system';

/**
 * Welche Cockpits stehen diesem Konto offen?
 *
 * Angeboten wird nur, was der eigene Rang deckt — nach oben gibt es
 * nichts zu klicken, auch nicht ausgegraut. Ein Knopf, der nie
 * funktioniert, ist schlechter als kein Knopf.
 *
 * `system` hängt am Agentur-Flag (`is_service_admin`, 0019) und nicht am
 * Rang: es ist kein Kundenrang, sondern der Wartungszugang.
 *
 * Die Liste ist aufsteigend sortiert, damit die Oberfläche sie
 * unverändert anzeigen kann.
 */
export function verfuegbareCockpits(opts: {
  role: StaffRole | null | undefined;
  isServiceAdmin?: boolean;
}): Cockpit[] {
  // Wer keinen Rang hat, bekommt gar nichts — auch nicht den Service: der
  // Bot-Betreiber, die Ape-Aushilfe und jede unbekannte Rolle. POSITIV
  // formuliert (Runde 5, B-3): Bis dahin stand hier `role === 'chatbot'`, und
  // jede neue Rolle wäre still an den Tresen gekommen.
  if (!hatMindestensRang(opts.role, 'staff')) return [];

  const offen: Cockpit[] = ['service'];
  if (hatMindestensRang(opts.role, 'manager')) offen.push('manager');
  if (hatMindestensRang(opts.role, 'admin')) offen.push('inhaber');
  if (opts.isServiceAdmin === true) offen.push('system');
  return offen;
}

/**
 * DIE COCKPIT-WEICHE (06.08.2026) — gehört dieses Konto auf diesem Gerät
 * ins Cockpit oder an den Tresen?
 *
 * ⚠️ WARUM ES DIESE FUNKTION GIBT: Die Regel stand bis hierher ZWEIMAL
 * wortgleich in der App — einmal als Startweiche, einmal als Rückweg aus
 * dem Service. Beim Reparieren der Tag-Fläche wäre eine dritte Kopie
 * dazugekommen. Til hat entschieden, sie zusammenzuziehen.
 *
 * ⚠️ SIE PRÜFT `hatMindestensRang(…, 'manager')` UND NICHTS ANDERES, und
 * das ist keine Geschmacksfrage: Das Rollentor am Ziel
 * (`apps/pass/src/app/(kontor)/_layout.tsx`) prüft genau diesen Rang.
 * Rechnete die Weiche mit einem anderen Prädikat, schickte sie jemanden
 * ins Cockpit, den das Layout im selben Atemzug zurückwürfe — eine
 * Schleife, die sich als Flackern zeigt.
 *
 * Fail-closed: Eine noch nicht geladene Rolle (`null`/`undefined`) ist
 * ein Nein. Wer hier falsch abbiegt, steht schlimmstenfalls vor dem
 * Grundriss statt vor einer Kachelwand — die harmlosere Richtung.
 *
 * ⚠️ KEINE Routen-Strings und KEIN `Platform` hier drin. `/heute` ist ein
 * expo-router-Pfad und in der Web-App bedeutungslos; `Platform` käme aus
 * react-native, und `coreGuard.test.ts` verbietet das in core zu Recht.
 * Beides liegt in `apps/pass/src/lib/cockpitWeiche.ts`.
 */
export function cockpitErreichbar(args: {
  istTelefon: boolean;
  rolle: StaffRole | null | undefined;
}): boolean {
  return args.istTelefon && hatMindestensRang(args.rolle, 'manager');
}
